/**
 * useRowsEditor — the state of an editor for a value relation's rows (times,
 * contacts): rows whose content IS the field's value, edited in place.
 *
 * It keeps an editable copy of the rows, which line is open, the row being
 * added, and what changed. Nothing is written until the host takes `handover`
 * (on Done): an `{ add, remove, update }` delta naming only what changed — new
 * rows without an id, removed ids, and edited rows by id with just their
 * changed columns. An edited row keeps its id, so core updates it in place; an
 * untouched row is never sent, so a stale copy can't overwrite someone else's
 * change.
 *
 * One line is open at a time: a row (by key) or the add line (ADD). The add
 * line holds a pending row; once it's given (typed, or set through its inputs)
 * it counts towards the handover, added or not.
 *
 * A relation supplies what differs: its columns, how a new row is written, a
 * blank row to start from, and optionally an order and a column comparison.
 */
import { computed, reactive, ref, type UnwrapRef } from "vue"

/** What a rows editor hands over: the write for `records.update`. */
export interface RowsDelta {
  add: Record<string, unknown>[]
  remove: { id: number | string }[]
  update: Record<string, unknown>[]
}

type Row = { id?: number | string | null }

/** One row in the copy: the stored row (`orig`, none for a new one) and its
 *  current value. */
export interface DraftRow<R extends Row> {
  key: string
  id: number | string | null
  orig: R | null
  cur: R
  removed: boolean
}

/** The add line's key in `open`. */
export const ADD = "add"

export interface RowsEditorOptions<R extends Row> {
  /** The columns a row can change in place. */
  columns: readonly (keyof R & string)[]
  /** A new row as core's write takes it. */
  toEntity: (row: R) => Record<string, unknown>
  /** The row the add line starts from, given the rows as listed. */
  blank: (rows: R[]) => R
  /** Whether a row has enough to be added (a value that reads, a start). */
  isComplete?: (row: R) => boolean
  /** Display order; stored order when absent. */
  order?: (a: R, b: R) => number
  /** Whether two values of a column are the same; `===` when absent. */
  same?: (column: string, a: unknown, b: unknown) => boolean
  /** The most rows the field holds (1 for a one-value field). */
  max?: number
}

export function useRowsEditor<R extends Row>(rows: R[], options: RowsEditorOptions<R>) {
  const same = options.same ?? ((_: string, a: unknown, b: unknown) => (a ?? null) === (b ?? null))

  const items = reactive(
    rows.map((row) => ({
      key: `row-${row.id}`,
      id: row.id ?? null,
      orig: row,
      cur: { ...row },
      removed: false,
    }))
  ) as DraftRow<R>[]

  const ordered = computed(() =>
    options.order ? [...items].sort((a, b) => options.order!(a.cur, b.cur)) : [...items]
  )
  const listed = computed(() => ordered.value.filter((r) => !r.removed))

  /** The one open line: a row's key, ADD, or none. A one-value field holding a
   *  value opens on it. */
  const open = ref<string | null>(options.max === 1 && items.length === 1 ? items[0]!.key : null)

  /** Open a line (closing the one open), or close them all with null. */
  function openLine(key: string | null) {
    open.value = key
  }

  /** Open a line, or close it when it's the one open. */
  function toggleLine(key: string) {
    open.value = open.value === key ? null : key
  }

  // --- What changed -------------------------------------------------------------

  function changedColumns(row: DraftRow<R>): Record<string, unknown> {
    const changes: Record<string, unknown> = {}
    if (!row.orig) return changes
    for (const column of options.columns) {
      if (!same(column, row.cur[column], row.orig[column])) {
        changes[column] = row.cur[column] ?? null
      }
    }
    return changes
  }

  const isNew = (row: DraftRow<R>) => row.id == null
  const isEdited = (row: DraftRow<R>) =>
    !isNew(row) && !row.removed && Object.keys(changedColumns(row)).length > 0

  // --- Editing the rows -----------------------------------------------------------

  function setRow(row: DraftRow<R>, value: R) {
    row.cur = value as UnwrapRef<R> & R
  }

  /** A new row is dropped; a stored one is marked removed, so it can come back. */
  function remove(row: DraftRow<R>) {
    if (open.value === row.key) open.value = null
    if (isNew(row)) items.splice(items.indexOf(row), 1)
    else row.removed = true
  }

  function restore(row: DraftRow<R>) {
    row.removed = false
  }

  // --- The add line -----------------------------------------------------------------

  const pending = ref(options.blank(listed.value.map((r) => r.cur))) as { value: R }
  /** Whether the new row has been given: typed, or set through its inputs. */
  const pendingGiven = ref(false)
  /** Bumped after each add, so the add line starts afresh. */
  const addCount = ref(0)

  /** Room for another row: a one-value field holding one has none. */
  const canAdd = computed(() => options.max == null || listed.value.length < options.max)

  function setPending(row: R) {
    pending.value = row
    pendingGiven.value = options.isComplete ? options.isComplete(row) : true
    open.value = ADD
  }

  /** Add the pending row to the list and start another. False when nothing
   *  was given. */
  function addPending(): boolean {
    if (!pendingGiven.value) return false
    addCount.value += 1
    items.push({
      key: `new-${addCount.value}`,
      id: null,
      orig: null,
      cur: pending.value,
      removed: false,
    } as DraftRow<R>)
    pending.value = options.blank(listed.value.map((r) => r.cur))
    pendingGiven.value = false
    open.value = null
    return true
  }

  // --- Handing over -------------------------------------------------------------

  /** Everything that changed, a given but un-added new row included. */
  const handover = computed<RowsDelta>(() => ({
    add: [
      ...items.filter((r) => isNew(r) && !r.removed).map((r) => options.toEntity(r.cur)),
      ...(pendingGiven.value && canAdd.value ? [options.toEntity(pending.value)] : []),
    ],
    remove: items.filter((r) => !isNew(r) && r.removed).map((r) => ({ id: r.id! })),
    update: items.filter(isEdited).map((r) => ({ id: r.id!, ...changedColumns(r) })),
  }))

  const dirty = computed(() => {
    const { add, remove, update } = handover.value
    return add.length + remove.length + update.length > 0
  })

  /** "1 new · 2 edited", or "No changes". */
  const summary = computed(() => {
    const { add, remove, update } = handover.value
    const parts = [
      add.length && `${add.length} new`,
      update.length && `${update.length} edited`,
      remove.length && `${remove.length} removed`,
    ].filter(Boolean)
    return parts.length ? parts.join(" · ") : "No changes"
  })

  return {
    items,
    ordered,
    listed,
    open,
    openLine,
    toggleLine,
    isNew,
    isEdited,
    setRow,
    remove,
    restore,
    pending,
    pendingGiven,
    addCount,
    canAdd,
    setPending,
    addPending,
    handover,
    dirty,
    summary,
  }
}

export type RowsEditor<R extends Row> = ReturnType<typeof useRowsEditor<R>>
